import classnames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useEffect } from 'react'

import { useStatsigDynamicConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-config.hook'
import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-locale-config.hook'
import { Icon, TOP_TIP_POSITION, ToolTipWrapper } from '@src/quince-components/src'
import CheckIcon from '@src/quince-components/src/atoms/select/icons/check_circle.svg'
import Product from '@src/quince-site-common/src/domains/Product'
import productHelper, { isProductAlcohol } from '@src/quince-site-common/src/helpers/productHelper'
import useEddExp from '@src/quince-site-common/src/hooks/edd/hooks/useEddExp'
import {
  generateEDDLabel,
  getCutoffTime,
  parseDateFromEddLabel,
} from '@src/quince-site-common/src/hooks/edd/utils/edd.utils'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { useZipCode } from '@src/quince-site-common/src/hooks/zipcode'
import { EDDDetails } from '@src/quince-site-common/src/services/eddService/eddService.types'
import Info from '@src/quince-site-integrated/src/assets/info_white.svg'
import { getCurrentFindingMethod } from '@src/telemetry/findingMethod'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'

// eslint-disable-next-line import/no-named-as-default
import ZipCodeButton from '../zipCodeButton'

import styles from './eddMessageContainer.module.scss'
import OrderCutoffTime from './orderCutOffTime'

export const DISABLE_EDD_FOR_AGE_RESTRICTED_PRODUCTS = 'disableForceEDDForAgeRestrictedProducts'

interface EDDMessageContainerProps {
  eddDetail: EDDDetails | undefined
  product: Product
  selectedInternalVariantId: number[] | undefined
  isQuickATCForMobile?: boolean
  containerClassName?: string
  showIcon?: boolean
  showNewPdpDesignV2?: boolean
  holidayMessageShown?: boolean
}

export function EDDMessageContainer({
  eddDetail,
  product,
  selectedInternalVariantId,
  isQuickATCForMobile,
  containerClassName,
  showIcon,
  showNewPdpDesignV2,
  holidayMessageShown = false,
}: EDDMessageContainerProps) {
  const { zipCode } = useZipCode()
  const { isEddExpEnabled: EDDExpEnabled } = useEddExp()
  const deviceInfo = useDeviceInfo()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const localeConfig = useStatsigDynamicLocaleConfig()

  // Check if EDD wrapping should be applied based on locale config
  const shouldApplyWrapping =
    localeConfig?.edd?.wrapping?.enabled && (!isDesktop || !localeConfig?.edd?.wrapping?.excludeDesktop)

  const selectedVariant = selectedInternalVariantId?.[0]?.toString()
  const selectedVariantObj = selectedInternalVariantId?.[0]
    ? product?.variants?.find(variant => variant.id === selectedInternalVariantId[0])
    : undefined

  const disableForceEDD = useStatsigDynamicConfig<boolean>(DISABLE_EDD_FOR_AGE_RESTRICTED_PRODUCTS)
  const isEDDExpEnabled = EDDExpEnabled || (productHelper.getIsAgeRestrictedProduct(product) && !disableForceEDD) // force edd for age restricted products

  const { t } = useTranslation('common', { keyPrefix: 'edd' })
  const { t: tCommon } = useTranslation('common')

  useEffect(() => {
    if (eddDetail?.showEDD) {
      const currentFindingMethod = getCurrentFindingMethod()
      trackEvent(TELEMETRY_EVENT_NAMES.IS_VISIBLE, {
        // @i18n-ignore
        action: 'product_page_edd',
        // @i18n-ignore
        category: 'edd',
        context: {
          product_id: product?.spid,
          variant_id: selectedVariantObj?.svid,
          internal_variant_id: selectedInternalVariantId?.[0],
          findingMethodAggregate: currentFindingMethod?.findingMethodAggregate,
          findingMethodGranular: currentFindingMethod?.findingMethodGranular,
          findingMethodId: currentFindingMethod?.findingMethodId,
          edd_standard_eligible: eddDetail.type === 'LB_STANDARD',
          edd_express_eligible: eddDetail.type === 'LB_SUPER',
          edd_max_days: eddDetail.max_days,
          edd_min_days: eddDetail.min_days,
          edd_max_date: eddDetail.max_date,
          edd_min_date: eddDetail.min_date,
          location_ids: eddDetail.inventoryLocations,
          productProperties: product && getProductPropertiesForTracking(product, selectedVariant),
          edd_label: generateEDDLabel(eddDetail, tCommon),
          is_serviceable: eddDetail.isServiceable,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    eddDetail?.showEDD,
    eddDetail?.type,
    eddDetail?.max_days,
    eddDetail?.min_days,
    product?.id,
    selectedVariant,
    selectedInternalVariantId,
    eddDetail?.max_date,
    eddDetail?.min_date,
    eddDetail?.inventoryLocations,
  ])

  if (!isEDDExpEnabled || eddDetail?.showEDD === undefined) return null

  if (!zipCode) {
    return (
      <div
        className={classnames(
          styles.eddMessageWrapper,
          containerClassName,
          isQuickATCForMobile && styles.eddMessageWrapper__quickATC,
          styles.eddMessageWrapper_adjustedPadding,
          showNewPdpDesignV2 && styles.eddMessageWrapper_newDesignV2,
        )}
      >
        <div>
          {showIcon && <Icon iconSvg={CheckIcon} className={styles.messageIcon} />}
          {t('in_stock_enter_zip_code')}
          <ZipCodeButton btnClassName={styles.zipcodeBtnClass}>{t('zip_code')}</ZipCodeButton> {t('for_delivery_info')}
        </div>
      </div>
    )
  }

  if (!eddDetail.isServiceable) {
    const zipCodeButton = (
      <ZipCodeButton btnClassName={classnames(styles.zipcodeBtnClass, styles.zipcodeBtnClass__unableToShip)}>
        {t('update_zip_code')}
      </ZipCodeButton>
    )
    const tooltip = (
      <ToolTipWrapper
        className={styles.toolTipWrapper}
        toolTipContentClassName={styles.tooltip}
        topTipPosition={TOP_TIP_POSITION.TOP}
        toolTipContentRenderer={() =>
          isProductAlcohol(product) ? t('unable_to_ship_wine_msg') : t('unable_to_ship_msg')
        }
      >
        <Info data-testid={`info-icon`} className={styles.infoIcon} />
      </ToolTipWrapper>
    )

    return (
      <div
        data-testid="unable-to-ship"
        className={classnames(
          styles.eddMessageWrapper,
          containerClassName,
          isQuickATCForMobile && styles.eddMessageWrapper__quickATC,
          styles.eddMessageWrapper_adjustedPadding,
          showNewPdpDesignV2 && styles.eddMessageWrapper_newDesignV2,
          styles.errorMessageWrapper,
          shouldApplyWrapping && styles.errorMessageWrapper__wrapStyling,
        )}
      >
        <div data-testid="unable-to-ship-message">{t('unable_to_ship', { zipCode: zipCode })}</div>
        {shouldApplyWrapping ? (
          <div className={styles.unableToShipActions__wrapStyling}>
            {zipCodeButton}
            {tooltip}
          </div>
        ) : (
          <>
            {' '}
            {zipCodeButton}
            {tooltip}
          </>
        )}
      </div>
    )
  }

  // Check if cutoff time has expired (cutoffDate exists but getCutoffTime returns null)
  const isCutoffExpired = eddDetail.cutoffDate && !getCutoffTime(eddDetail.cutoffDate, t)
  // Show "in stock ready to ship" message if EDD is not shown OR cutoff time has expired
  if ((!eddDetail.showEDD && zipCode) || isCutoffExpired) {
    return (
      <div
        className={classnames(
          styles.eddMessageWrapper,
          containerClassName,
          isQuickATCForMobile && styles.eddMessageWrapper__quickATC,
          styles.eddMessageWrapper_adjustedPadding,
          showNewPdpDesignV2 && styles.eddMessageWrapper_newDesignV2,
        )}
      >
        <div>
          {showIcon && <Icon iconSvg={CheckIcon} className={styles.messageIcon} />}
          {t('in_stock_and_ships_free_to')}{' '}
          <ZipCodeButton btnClassName={styles.zipcodeBtnClass}>{zipCode}</ZipCodeButton>
        </div>
      </div>
    )
  }

  if (eddDetail.isExpressShipping) {
    return (
      <div
        className={classnames(
          styles.eddMessageWrapper,
          containerClassName,
          isQuickATCForMobile && styles.eddMessageWrapper__quickATC,
          styles.eddMessageWrapper_adjustedPadding,
          showNewPdpDesignV2 && styles.eddMessageWrapper_newDesignV2,
        )}
      >
        <div>
          {showIcon && <Icon iconSvg={CheckIcon} className={styles.messageIcon} />}
          {generateEDDLabel(eddDetail, tCommon)} {tCommon('with_priority_shipping_to')}{' '}
          <ZipCodeButton btnClassName={styles.zipcodeBtnClass}>{zipCode}</ZipCodeButton>
        </div>
      </div>
    )
  }

  if (eddDetail.type === 'LB_STANDARD_PREMIUM' && eddDetail.max_cal_days === 2) {
    const label = generateEDDLabel(eddDetail, tCommon) ?? ''
    const { before, date, after } = parseDateFromEddLabel(label)
    return (
      <div
        className={classnames(
          styles.eddMessageWrapper,
          containerClassName,
          isQuickATCForMobile && styles.eddMessageWrapper__quickATC,
          styles.eddMessageWrapper_adjustedPadding,
          showNewPdpDesignV2 && styles.eddMessageWrapper_newDesignV2,
        )}
      >
        <div
          className={classnames(
            styles.eddMessageWrapper__callOut,
            showNewPdpDesignV2 && styles.eddMessageWrapper__callOut_newDesignV2,
          )}
        >
          <div>
            {before}
            <strong className={styles.eddMessageWrapper__bold}>{date}</strong>
            {after}
          </div>
          {eddDetail.cutoffDate && <OrderCutoffTime cutoffDate={eddDetail.cutoffDate} />}
        </div>
        <div>
          {showIcon && <Icon iconSvg={CheckIcon} className={styles.messageIcon} />}
          {t('in_stock_and_ships_free_to')}{' '}
          <ZipCodeButton btnClassName={styles.zipcodeBtnClass}>{zipCode}</ZipCodeButton>
        </div>
      </div>
    )
  }

  // Calculate EDD message length for conditional styling (only on desktop)
  const eddLabel = generateEDDLabel(eddDetail, tCommon) ?? ''
  const toText = tCommon('to')
  const cutoffTimeText = eddDetail?.cutoffDate ? (getCutoffTime(eddDetail.cutoffDate, t) ?? '') : ''
  const messageText = `${eddLabel} ${toText} ${zipCode}${cutoffTimeText ? ` . ${cutoffTimeText}` : ''}`
  //max chars the flex can accomate with cutoff for edd message, anything over this should be wrapped like existing
  // Only apply cutoff style on desktop when message length <= 69 and not in cutoff wrap locales
  const shouldApplyCutoffStyle =
    holidayMessageShown && isDesktop && !localeConfig?.edd?.cutoffWrap && messageText.length <= 69
  return (
    <div className={showIcon ? styles.eddDefaultContainer : ''}>
      {showIcon && <Icon iconSvg={CheckIcon} className={styles.messageIcon} />}
      <div
        className={classnames(
          styles.eddMessageWrapper,
          containerClassName,
          isQuickATCForMobile && styles.eddMessageWrapper__quickATC,
          styles.eddMessageWrapper_adjustedPadding,
          showNewPdpDesignV2 && styles.eddMessageWrapper_newDesignV2,
        )}
      >
        <div className={shouldApplyCutoffStyle ? styles.eddMessageWithCutoff : ''}>
          {generateEDDLabel(eddDetail, tCommon)} {tCommon('to')}{' '}
          <ZipCodeButton
            btnClassName={classnames(
              styles.zipcodeBtnClass,
              shouldApplyCutoffStyle ? styles.zipcodeBtnClass__cutoff : '',
            )}
          >
            {zipCode}
          </ZipCodeButton>
          {eddDetail.cutoffDate && (
            <>
              <span className={shouldApplyCutoffStyle ? styles.desktopOnlyMsg : styles.hideDesktopMsg}>{'.'}</span>
              <OrderCutoffTime cutoffDate={eddDetail.cutoffDate} />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
